import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Icon } from 'components/Icon';
import { signOut } from 'components/Login';
import { appendChild, type JSXChild, render } from 'engine/index';
import { Hourglass, RefreshCcw, Store, Trophy, X } from 'lucide-static';
import type { KwaliteitenJachtCard, KwaliteitenJachtUpdate } from 'shared/modelTypes/kwaliteiten-jacht';
import { lateinit, unreachable } from 'shared/util';
import { Game } from './game';
import { rpc, State } from 'engine/rpc';
import { Timer } from 'components/Timer';
import { marked } from 'marked';

export class KwaliteitenJacht extends Game<'kwaliteiten-jacht'> {
	override maxPlayers: number = 12;

	kwaliteitenEl: HTMLElement = lateinit;
	scoreEl: HTMLElement = lateinit;
	view: HTMLElement = lateinit;

	closeModal?: () => void;

	override getGameName(): string {
		return 'Kwaliteiten Jacht';
	}

	override defineBottomUI(): JSXChild {
		return (
			<>
				<StatBubble
					color="yellow"
					label="Kwaliteiten"
					value="0"
					id="kwaliteiten-count"
					onClick={() =>
						showModal((confirm) => {
							// close previous modal
							if (this.closeModal) this.closeModal();
							this.closeModal = confirm;
							return <KwaliteitenList label="Kwaliteiten" cards={State.kwaliteitenCards} />;
						})
					}
				/>
				<StatBubble color="green" label="Munten" value="0" id="score" />
			</>
		);
	}
	override onShellCreated(view: HTMLElement): void {
		super.onShellCreated(view);
		this.kwaliteitenEl = view.querySelector('#kwaliteiten-count')!;
		this.scoreEl = view.querySelector('#score')!;
		this.view = view;
	}

	override handleUpdate(update: KwaliteitenJachtUpdate): void {
		const sessionId = State.gameId.toString();
		const playerId = State.playerId;
		const kind = update.kind;
		// const playerId = State.playerId;

		// console.log('message', update);

		switch (kind) {
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				this.showRegisterScreen();
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
			case 'update-player-state':
				State.name = update.player.name;
				State.color = update.player.color;
				State.kwaliteitenCards = update.player.cards;
				State.score = update.player.coins;

				this.shellNameEl.innerText = update.player.name;
				setUserIconColor(this.userIconEl, update.player.color);
				this.kwaliteitenEl.innerText = State.kwaliteitenCards.length.toString();
				this.scoreEl.innerText = (State.score ?? 0).toString();
				break;
			case 'show-story-screen':
				{
					if (this.closeModal) {
						this.closeModal();
						this.closeModal = undefined;
					}

					const image = (() => {
						if (update.image) {
							return `/kwaliteiten-jacht/${update.image}.png`;
						}
						unreachable(update.image);
					})();

					render(this.shellContentEl, <StoryScreen title={update.title} message={update.message} image={image} />);
				}
				break;
			case 'show-swap-screen':
				if (this.closeModal) this.closeModal();
				render(
					this.shellContentEl,
					<SwapScreen timerFrom={update.timerFrom} timerTo={update.timerTo} round={update.round} text={update.text} />,
				);
				break;
			case 'show-cards-screen':
				{
					const playerPickModal = (chosenCard: string = '') => {
						const players = (update.playerOptions ?? []).filter((p) => p.color !== State.color);

						return new Promise<{ name: string; color: string } | undefined>((resolve) => {
							// close previous modal
							if (this.closeModal) this.closeModal();

							showModal(
								(confirm, _cancel) => {
									this.closeModal = confirm;
									return (
										<div class="rounded-md bg-white p-4 shadow-md">
											{chosenCard && <small class="text-gray-700">{chosenCard}</small>}
											<h2 class="mb-4 text-lg font-bold">
												{(() => {
													switch (update.action) {
														case 'find-doubles':
															return 'Wie heeft deze kwaliteit ook?';
														case 'trade-card':
															return 'Aan wie schenk je deze kaart?';
														case 'buy-card':
															return 'Van wie wil je een kaart kopen?';
													}
												})()}
											</h2>
											<div class="flex flex-col gap-2">
												{players.map((p) => (
													<button
														class="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-center font-semibold text-gray-800"
														on:click={() => {
															resolve(p);
															confirm();
														}}
													>
														{p.name}
													</button>
												))}
											</div>
										</div>
									);
								},
								{
									passiveDismiss: true,
								},
							).then(() => resolve(undefined));
						});
					};

					const handleCardTap = (() => {
						const action = update.action;
						switch (action) {
							case 'none':
								return undefined;
							case 'buy-card':
								return undefined;
							case 'find-doubles':
								return async (card: string) => {
									const otherPlayer = await playerPickModal(card);
									// console.log('got player', otherPlayer);
									if (!otherPlayer) return;
									rpc.sendKwaliteitenJachtMessage.query({
										sessionId,
										update: {
											kind: 'double-found',
											playerId,
											otherPlayerColor: otherPlayer.color,
											cardName: card,
										},
									});
								};
							case 'trade-card':
								return async (card: string) => {
									const otherPlayer = await playerPickModal(card);
									// console.log('got player', otherPlayer);
									if (!otherPlayer) return;
									rpc.sendKwaliteitenJachtMessage.query({
										sessionId,
										update: {
											kind: 'send-trade-offer',
											playerId,
											otherPlayerColor: otherPlayer.color,
											cardName: card,
										},
									});
								};
							default:
								unreachable(action);
						}
					})();

					let handleShopTap = undefined;
					if (update.action === 'buy-card') {
						handleShopTap = async () => {
							const otherPlayer = await playerPickModal('');
							if (!otherPlayer) return;

							showModal((confirm) => {
								this.closeModal = confirm;
								return (
									<div class="rounded-md bg-white p-4 shadow-lg">
										<h2>
											Wacht op <strong>{otherPlayer.name}</strong> om een kaart te kiezen
										</h2>
									</div>
								);
							});

							rpc.sendKwaliteitenJachtMessage.query({
								sessionId,
								update: {
									kind: 'send-purchase-request',
									playerId,
									otherPlayerColor: otherPlayer.color,
								},
							});
						};
					}

					render(
						this.shellContentEl,
						<ShareCardsScreen
							title={update.title}
							onShopTap={handleShopTap}
							description={update.description}
							cards={update.cards}
							onCardTap={handleCardTap}
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
						/>,
					);
				}
				break;
			case 'show-results-screen':
				render(this.shellContentEl, <ResultsScreen results={update.players} />);

				break;
			case 'show-message':
				appendChild(this.view, <ShowMessage head={update.head} body={update.body} />);
				break;

			case 'show-qualitypicker-screen':
				{
					if (this.closeModal) {
						this.closeModal();
						this.closeModal = undefined;
					}

					render(
						this.shellContentEl,
						<QualityPickerScreen
							title={update.title}
							message={update.message}
							cards={update.cards}
							updateSelection={(selected) => {
								rpc.sendKwaliteitenJachtMessage.query({
									sessionId,
									update: {
										kind: 'update-relevant-cards',
										playerId,
										relevantCards: selected,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'display-trade-offer':
				// close previous modal
				if (this.closeModal) this.closeModal();
				showModal((confirm) => {
					this.closeModal = confirm;
					return (
						<div class="rounded-md bg-white p-4 shadow-lg">
							<h2>
								<strong>{update.player.name}</strong> schenkt jou het volgende kaartje: <strong>{update.card}</strong>
							</h2>
							<small>Kies een kaart om weg te geven.</small>
							<div class="flex flex-col gap-2">
								{State.kwaliteitenCards.map((c) => (
									<button
										on:click={() => {
											rpc.sendKwaliteitenJachtMessage.query({
												sessionId,
												update: {
													kind: 'finalize-trade',
													playerId,
													otherPlayerColor: update.player.color,
													giveCard: c.name,
												},
											});
											confirm();
										}}
										class="border border-gray-300 bg-gray-50 px-3 py-2 font-semibold"
									>
										{c.name}
									</button>
								))}
							</div>
							<button
								on:click={() => {
									rpc.sendKwaliteitenJachtMessage.query({
										sessionId,
										update: {
											kind: 'finalize-trade',
											playerId,
											otherPlayerColor: update.player.color,
											giveCard: '',
										},
									});
									confirm();
								}}
								class="mx-auto mt-3 bg-theme-600 px-3 py-2 font-semibold text-theme-50"
							>
								Annuleren
							</button>
						</div>
					);
				});

				break;
			case 'display-purchase-request':
				// close previous modal
				if (this.closeModal) this.closeModal();
				showModal((confirm) => {
					this.closeModal = confirm;
					return (
						<div class="rounded-md bg-white p-4 shadow-lg">
							<h2>
								<strong>{update.player.name}</strong> will een kaart van jou kopen.
							</h2>
							<small>
								Selecteer de kaart die zij willen hebben <em>of</em> de kat in de zak. Je mag niet weigeren.
							</small>
							<div class="flex flex-col gap-2">
								{State.kwaliteitenCards.map((c) => (
									<button
										on:click={() => {
											rpc.sendKwaliteitenJachtMessage.query({
												sessionId,
												update: {
													kind: 'finalize-purchase',
													playerId,
													otherPlayerColor: update.player.color,
													giveCard: c.name,
												},
											});
											confirm();
										}}
										class="border border-gray-300 bg-gray-50 px-3 py-2 font-semibold"
									>
										{c.name}
									</button>
								))}
							</div>
						</div>
					);
				});
				break;
			case 'finalized-purchase':
				if (this.closeModal) {
					this.closeModal();
				}
				break;
			default:
				unreachable(kind);
		}
	}
}

export function Waitscreen({ message }: { message: string }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">
				<span class="font-bold text-gray-900">{message}</span>
			</div>
		</div>
	);
}

export function SwapScreen({ round, text, timerFrom, timerTo }: { timerFrom: number; timerTo: number; round: number; text: string }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Timer timerFrom={timerFrom} timerTo={timerTo} />
			<h2 class="mb-2 text-3xl font-bold text-gray-900 sm:mb-6 sm:text-6xl">Ronde {round}</h2>
			<div class="text-center text-xl text-gray-800">
				<p>{text}</p>
			</div>
			<Icon icon={RefreshCcw} class="size-20 text-gray-300" />
		</div>
	);
}

export function StoryScreen({ title, message, image }: { title: string; message: string; image: string }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="mb-2 text-3xl font-bold text-gray-900 sm:mb-6 sm:text-6xl">{title}</h2>
			<pre class="w-full whitespace-normal text-left font-sans text-gray-800" __innerHTML={marked(message)}></pre>
			<img src={image} class="mt-4 size-64" />
		</div>
	);
}

export function ShareCardsScreen({
	title,
	description,
	cards,
	timerFrom,
	timerTo,
	onCardTap,
	onShopTap,
}: {
	title: string;
	description: string;
	cards: { name: string; color: string }[];
	timerFrom: number;
	timerTo: number;
	onCardTap?: (name: string) => void;
	onShopTap?: () => void;
}) {
	return (
		<div class="flex h-fit w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="mb-3 text-3xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">{title}</h2>
			<pre class="w-full whitespace-normal text-center font-sans text-gray-800" __innerHTML={marked(description)}></pre>
			{onShopTap && (
				<button
					on:click={onShopTap}
					class="flex gap-3 rounded-md bg-gradient-to-br from-yellow-400 via-orange-400 to-orange-600 px-3 py-2 font-semibold text-white shadow-md"
				>
					<Icon icon={Store} class="size-5 drop-shadow-md" />
					<span class="drop-shadow-md">Koop kaart</span>
				</button>
			)}
			<Timer timerFrom={timerFrom} timerTo={timerTo} />
			<div class="grid grid-cols-2 gap-3 lg:grid-cols-3">
				{cards.map((c) => (
					<Card card={c} onCardTap={onCardTap} />
				))}
			</div>
		</div>
	);
}

export function QualityPickerScreen({
	title,
	message,
	cards,
	updateSelection,
}: {
	title: string;
	message: string;
	cards: KwaliteitenJachtCard[];
	updateSelection: (selected: string[]) => void;
}) {
	const selected: Set<string> = new Set();

	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="mb-2 text-3xl font-bold text-gray-900 sm:mb-6 sm:text-6xl">{title}</h2>
			<pre class="w-full whitespace-normal text-left font-sans text-gray-800">{message}</pre>
			<Timer timerFrom={Date.now()} timerTo={Date.now() + 60000} />

			<div class="grid grid-cols-2 gap-3 lg:grid-cols-3">
				{cards.map((c) => {
					const view: HTMLElement = (
						<div class="cursor-pointer rounded-md opacity-70 transition-transform duration-150 hover:scale-[101%] hover:opacity-100 hover:shadow-md data-[selected]:scale-[102%] data-[selected]:opacity-100 data-[selected]:shadow-sm data-[selected]:outline data-[selected]:outline-theme-900">
							<Card
								card={c}
								onCardTap={() => {
									view.toggleAttribute('data-selected');
									if (view.hasAttribute('data-selected')) {
										selected.add(c.name);
									} else {
										selected.delete(c.name);
									}
									updateSelection([...selected]);
								}}
							/>
						</div>
					);
					return view;
				})}
			</div>
		</div>
	);
}

export function ResultsScreen({ results }: { results: { name: string; color: string; score: number }[] }) {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Einduitslag</h2>
			<table class="overflow-hidden rounded-md shadow-sm">
				<thead>
					<tr class="bg-gray-100">
						<th class="px-5 py-1">Rank</th>
						<th class="px-5 py-1">Pion</th>
						<th class="px-5 py-1">Naam</th>
						<th class="px-5 py-1">Punten</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r, i) => {
						const highlight = State.color == r.color;

						return (
							<tr class={highlight ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'even:bg-gray-50'}>
								<td class="px-5 py-1">
									<div class="inline-flex items-baseline gap-3">
										<span class="font-bold">{i + 1}</span>
										{i == 0 && <Icon icon={Trophy} class="size-4 text-orange-400" />}
										{i == 1 && <Icon icon={Trophy} class="size-4 text-gray-400" />}
										{i == 2 && <Icon icon={Trophy} class="size-4 text-orange-700" />}
										{i >= 3 && <Icon icon={Trophy} class="size-4 opacity-0" />}
									</div>
								</td>
								<td class="px-5 py-1">{__(['playerColor.' + r.color])}</td>
								<td class="px-5 py-1">{r.name}</td>
								<td class="px-5 py-1">{r.score}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
				Spel afsluiten
			</button>
		</div>
	);
}

function Card({ card, onCardTap }: { card: KwaliteitenJachtCard; onCardTap?: (name: string) => void }) {
	// HACK: CSS does not allow us to hyphenate words only when they don't fit in a container
	// so this is a cheap trick to mostly emulate that behavior
	// we find words that are longer than what fits and enable hyphenation specifically word that word
	const maxWordLength = 15;
	const words = card.name.split(' ').map((word) => <span class={word.length > maxWordLength ? 'hyphens-auto' : ''}>{word} </span>);
	return (
		<div
			on:click={!onCardTap ? undefined : () => onCardTap(card.name)}
			class="flex size-full h-20 flex-col items-center justify-center rounded-md border-2 bg-white p-4 text-center text-sm font-semibold shadow-sm transition-all data-[taken]:pointer-events-none data-[taken]:opacity-30 md:text-lg"
		>
			<span>{words}</span>
		</div>
	);
}

function KwaliteitenList({ label, cards }: { label: string; cards: KwaliteitenJachtCard[] }) {
	return (
		<div class="relative flex max-h-full w-full max-w-md flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg">
			<button action-cancel class="absolute right-2 top-2 rounded-md p-2">
				<Icon icon={X} class="size-6" />
			</button>
			<h3 class="mb mb-6 text-lg font-bold text-gray-900">{label}</h3>
			<div class="grid grid-cols-2 gap-4">
				{cards.map((c) => (
					<Card card={c} />
				))}
			</div>
			{!cards.length && <small class="text-center">Nog geen kaarten.</small>}
		</div>
	);
}

export function ShowMessage({ head, body }: { head: string; body: string }) {
	const view = (
		<div
			popup-message
			class="absolute bottom-10 left-1/2 flex w-4/5 -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg"
		>
			<button
				action-cancel
				class="absolute right-2 top-2 rounded-md p-2"
				on:click={(event: MouseEvent) => {
					const btn = event.target as HTMLElement;
					const closePopUp = btn.closest('[popup-message]');
					if (closePopUp) {
						closePopUp.remove();
					}
				}}
			>
				<Icon icon={X} class="size-6" />
			</button>
			<h3 class="mb mb-2 text-lg font-bold text-gray-900">{head}</h3>
			<p class="w-full p-1 text-center">{body}</p>
		</div>
	);

	return view;
}

// export function initKwaliteitenJacht() {
// 	render(
// 		app,
// 		<AppShell
// 			gameName="Kwaliteiten jacht"
// 			bottomUI={
// 				<div class="flex size-16 flex-col justify-center rounded-full bg-theme-600 text-center leading-none text-theme-50 shadow-sm shadow-theme-700/30">
// 					<span coins class="text-lg">
// 						0
// 					</span>
// 					<small class="text-[9px]">Coins</small>
// 				</div>
// 			}
// 		>
// 			{/* <Waitscreen
// 			message="Wacht totdat alle spelers zijn aangemeld en de spelleider het
// verhaal start."
// 		/> */}
// 			{/* <StoryScreen
// 			title="Dag 1"
// 			message="Als echte potentiële marktkoopmannen en -vrouwen ontstond een
// eerste verkenning. Wie heeft welke kwaliteiten op dit moment in
// handen? Loop de ruimte door spreek 1 op 1 iemand aan en vraag
// welke kwaliteiten ze momenteel in handen hebben. Deel ook jouw
// kwaliteiten maar laat je scherm niet zien. Heb je de kat in de zak
// kaart? Deel dit nog met niemand. Jullie krijgen 5 minuten de tijd
// om zoveel mogelijk te ontdekken."
// 			image="/logo.svg"
// 		/> */}
// 			{/* <ShareCardsScreen
// 			title="Ronde 1:"
// 			description="deel jouw kwaliteiten met de andere speler en ga op
// onderzoek welke kwaliteiten de anderen in handen hebben."
// 			cards={[
// 				//
// 				{ name: 'Doorzettingsvermogen hebben', color: '' },
// 				{ name: 'Vriendelijk', color: '' },
// 				{ name: 'opdrachten uitvoeren', color: '' },
// 				{ name: 'op informatie gericht zijn', color: '' },
// 				{ name: 'mensen motiveren', color: '' },
// 				{ name: 'Natuurkundig en wiskundig inzicht', color: '' },
// 				{ name: 'Ambitieus zijn', color: '' },
// 			]}
// 		/> */}
// 			{/* <StoryScreen
// 			title="Dag 2 & 3"
// 			message="Na de eerste dag is er al veel gedeeld! Tijd om een 2e
// verkennende ronde te starten. Tijdens deze ronde gaan jullie ga je
// op zoek naar kwaliteiten die voor jou van toepassing zijn maar je
// let vooral op de dubbele kwaliteiten. Heb jij een speler gevonden
// die dezelfde kaart heeft dan win je 2 muntstukken. Heb je de kat
// in de zak kaart? Deel dit nog met niemand. Jullie krijgen 5
// minuten de tijd om zoveel mogelijk te ontdekken."
// 			image="/logo.svg"
// 		/> */}
// 			{/* <ShareCardsScreen
// 			title="Ronde 1:"
// 			description="Ga op zoek naar kaarten die anderen mensen ook hebben. Druk op een dubelle kaart en geef aan wie deze ook heeft. Dan krijg je 2 punten."
// 			// TODO: enable UI for selecting double cards
// 			cards={[
// 				//
// 				{ name: 'Doorzettingsvermogen hebben', color: '' },
// 				{ name: 'Vriendelijk', color: '' },
// 				{ name: 'opdrachten uitvoeren', color: '' },
// 				{ name: 'op informatie gericht zijn', color: '' },
// 				{ name: 'mensen motiveren', color: '' },
// 				{ name: 'Natuurkundig en wiskundig inzicht', color: '' },
// 				{ name: 'Ambitieus zijn', color: '' },
// 			]}
// 		/> */}
// 			{/* <StoryScreen
// 			title="Dag 4:"
// 			message="Tijdens de eerste dagen zijn de verschillende kwaliteiten
// opgevallen. Je ziet dat de boer oog heeft voor de natuur, of dat de
// kledingmaker erg creatief is. Welke kwaliteiten zijn jou opgevallen
// binnen de groep van marktkoopmannen- en vrouwen? Geef een
// kwaliteit weg aan de ander.
// Heb jij een speler gevonden waar jij een kwaliteit aan kunt
// schenken dan win je 2 muntstukken. Je krijgt daarnaast ook een
// kwaliteit terug. Heb je de kat in de zak kaart? Deel dit nog met
// niemand. Jullie krijgen 5 minuten de tijd om zoveel mogelijk uit te
// delen die niet voor jou van toepassing zijn."
// 			image="/logo.svg"
// 		/> */}
// 			{/* <ShareCardsScreen
// 			title="Ronde 1:"
// 			description="deel jouw kwaliteiten met de andere speler en ga op
// onderzoek welke kwaliteiten de anderen in handen hebben."
// 			// TODO: UI for initiating a trade and accepting/declining a trade
// 			cards={[
// 				//
// 				{ name: 'Doorzettingsvermogen hebben', color: '' },
// 				{ name: 'Vriendelijk', color: '' },
// 				{ name: 'opdrachten uitvoeren', color: '' },
// 				{ name: 'op informatie gericht zijn', color: '' },
// 				{ name: 'mensen motiveren', color: '' },
// 				{ name: 'Natuurkundig en wiskundig inzicht', color: '' },
// 				{ name: 'Ambitieus zijn', color: '' },
// 			]}
// 		/> */}
// 			<StoryScreen
// 				title="Dag 7"
// 				message="De 7e dag staan we met zijn allen op de markt. Maak een grote
// cirkel en nodig de dorpelingen uit om 1 voor 1 het marktplein in
// het midden te betreden. Dit doe je door letterlijk naar voren te
// stappen en te vertellen wie je bent. Welke functie bekleed je in het
// dorpje Gamehaven (bakker, schilder, bankier, etc.) en welke
// kwaliteiten heb jij richting de dorpelingen te bieden, kies er
// minimaal 3 uit die voor jou van toepassing zijn. Download
// hieronder jouw kwaliteitskaart."
// 				button={
// 					<button
// 						class="rounded-md bg-theme-600 p-4 text-theme-50"
// 						on:click={() => downloadString('kwaliteitskaart', 'kwaliteitskaart.txt', 'text/plain')}
// 					>
// 						Download
// 					</button>
// 				}
// 				image="/logo.svg"
// 			/>
// 			{/* <ResultsScreen
// 			results={[
// 				{ name: 'foo', color: 'red', score: 16 },
// 				{ name: 'bar', color: 'green', score: 14 },
// 				{ name: 'baz', color: 'blue', score: 12 },
// 				{ name: 'qux', color: 'yellow', score: 11 },
// 				{ name: 'quux', color: 'purple', score: 9 },
// 			]}
// 		/> */}
// 		</AppShell>,
// 	);

// 	throw '';
// }
